import { useState, useEffect, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  Box, Button, Card, Typography, TextField, Autocomplete, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, alpha, InputAdornment, MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { Invoice, CreateInvoiceDto } from '../../types/b2b';

interface Product { id: string; name: string; sku: string; price: number; }
interface Customer { id: string; firstName: string; lastName: string; email: string; }

const fetchInvoice = async (id: string): Promise<Invoice> => {
  const response = await axiosInstance.get(`/invoices/${id}`);
  return response.data;
};

const fetchProducts = async (): Promise<Product[]> => {
  const response = await axiosInstance.get('/products');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchCustomers = async (): Promise<Customer[]> => {
  const response = await axiosInstance.get('/customers');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

export const InvoiceEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const { data: invoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => fetchInvoice(id!),
    enabled: !!id,
  });

  const { data: products = [] } = useQuery({ queryKey: ['products'], queryFn: fetchProducts });
  const { data: customers = [] } = useQuery({ queryKey: ['customers'], queryFn: fetchCustomers });

  const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<CreateInvoiceDto>({
    defaultValues: {
      customerId: '',
      paymentTerms: 'net_30',
      notes: '',
      items: [{ productId: '', quantity: 1, unitPrice: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'items' });
  const items = watch('items');

  useEffect(() => {
    if (invoice) {
      const mappedItems = invoice.items?.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      })) || [{ productId: '', quantity: 1, unitPrice: 0 }];

      reset({
        customerId: invoice.customerId,
        paymentTerms: invoice.paymentTerms || 'net_30',
        notes: invoice.notes || '',
        items: mappedItems,
      });

      if (invoice.customer) {
        setSelectedCustomer({
          id: invoice.customerId,
          firstName: invoice.customer.firstName,
          lastName: invoice.customer.lastName,
          email: invoice.customer.email || '',
        });
      }
    }
  }, [invoice, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: CreateInvoiceDto) => axiosInstance.patch(`/invoices/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      showMessage('Invoice updated successfully', 'success');
      navigate(`/app/invoices/show/${id}`);
    },
    onError: (error: any) => showMessage(error.response?.data?.message || 'Failed to update invoice', 'error'),
  });

  const onSubmit = (data: CreateInvoiceDto) => {
    const validItems = (data.items || []).filter((item) => item.productId && item.quantity > 0);
    if (validItems.length === 0) {
      showMessage('Please add at least one item', 'error');
      return;
    }
    updateMutation.mutate({ ...data, items: validItems });
  };

  const handleProductChange = (index: number, product: Product | null) => {
    if (product) {
      setValue(`items.${index}.productId`, product.id);
      setValue(`items.${index}.unitPrice`, product.price);
    } else {
      setValue(`items.${index}.productId`, '');
      setValue(`items.${index}.unitPrice`, 0);
    }
  };

  const calculateSubtotal = (index: number): number => {
    const itemsArr = items || [];
    const quantity = itemsArr[index]?.quantity || 0;
    const unitPrice = itemsArr[index]?.unitPrice || 0;
    return quantity * unitPrice;
  };

  const calculateTotal = (): number => {
    return (items || []).reduce((sum, _, index) => sum + calculateSubtotal(index), 0);
  };

  const colors = mode === 'dark'
    ? { surface: palette.surface, surfaceElevated: palette.surfaceElevated, border: palette.border, borderLight: palette.borderLight, textPrimary: palette.textPrimary, textMuted: palette.textMuted }
    : { surface: palette.light.surface, surfaceElevated: palette.light.surfaceElevated, border: palette.light.border, borderLight: palette.light.borderLight, textPrimary: palette.light.textPrimary, textMuted: palette.light.textMuted };

  const formatCurrency = (amount: number) => new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD' }).format(amount);
  const textFieldSx = { '& .MuiOutlinedInput-root': { backgroundColor: colors.surfaceElevated, border: `1px solid ${colors.border}`, borderRadius: theme.radius.sm, '&:hover': { borderColor: palette.primary }, '& fieldset': { border: 'none' } }, '& .MuiInputBase-input': { color: colors.textPrimary }, '& .MuiInputLabel-root': { color: colors.textMuted } };

  if (isLoadingInvoice) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><CircularProgress sx={{ color: palette.primary }} /></Box>;
  }

  if (!invoice) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><Typography variant="h5" sx={{ color: colors.textPrimary }}>Invoice not found</Typography></Box>;
  }

  if (invoice.status !== 'draft') {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ color: colors.textPrimary, mb: 2 }}>This invoice cannot be edited</Typography>
        <Typography sx={{ color: colors.textMuted, mb: 4 }}>Only draft invoices can be edited.</Typography>
        <Button variant="contained" onClick={() => navigate(`/app/invoices/show/${id}`)} sx={{ backgroundColor: palette.primary }}>View Invoice</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ ...animations.fadeIn }}>
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <IconButton onClick={() => navigate('/app/invoices')} sx={{ border: `1px solid ${colors.border}`, borderRadius: theme.radius.sm, color: colors.textPrimary, '&:hover': { borderColor: palette.primary, backgroundColor: alpha(palette.primary, 0.1) } }}><ArrowBackIcon /></IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReceiptIcon sx={{ color: palette.primary, fontSize: 20 }} />
              <Typography variant="overline" sx={{ color: palette.primary, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, letterSpacing: typography.letterSpacing.widest, fontWeight: typography.fontWeight.bold }}>EDIT INVOICE</Typography>
            </Box>
            <Typography variant="h4" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.extrabold, color: colors.textPrimary, letterSpacing: '-0.02em' }}>{invoice.invoiceNumber}</Typography>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Customer Selection */}
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.1) }}>
          <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 2 }}>CUSTOMER</Typography>
          <Controller
            name="customerId"
            control={control}
            rules={{ required: 'Customer is required' }}
            render={({ field }) => (
              <Autocomplete
                options={customers}
                value={selectedCustomer}
                onChange={(_, newValue) => {
                  setSelectedCustomer(newValue);
                  field.onChange(newValue?.id || '');
                }}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                renderInput={(params) => <TextField {...params} label="Select Customer" error={!!errors.customerId} helperText={errors.customerId?.message} sx={textFieldSx} />}
                sx={{ '& .MuiAutocomplete-popupIndicator': { color: colors.textMuted } }}
              />
            )}
          />
        </Card>

        {/* Line Items */}
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, overflow: 'hidden', mb: 3, ...animations.slideUp(0.15) }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>LINE ITEMS</Typography>
            <Button startIcon={<AddIcon />} onClick={() => append({ productId: '', quantity: 1, unitPrice: 0 })} variant="outlined" size="small" sx={{ borderColor: palette.primary, color: palette.primary }}>Add Item</Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.surfaceElevated }}>
                  <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs }}>PRODUCT</TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, width: 120 }}>QUANTITY</TableCell>
                  <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, width: 150 }}>UNIT PRICE</TableCell>
                  <TableCell align="right" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, width: 150 }}>SUBTOTAL</TableCell>
                  <TableCell sx={{ width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Controller
                        name={`items.${index}.productId`}
                        control={control}
                        render={({ field: productField }) => (
                          <Autocomplete
                            options={products}
                            value={products.find((p) => p.id === productField.value) || null}
                            onChange={(_, newValue) => handleProductChange(index, newValue)}
                            getOptionLabel={(option) => `${option.name} (${option.sku})`}
                            renderInput={(params) => <TextField {...params} placeholder="Select product" size="small" sx={textFieldSx} />}
                            size="small"
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`items.${index}.quantity`}
                        control={control}
                        render={({ field: qtyField }) => <TextField {...qtyField} type="number" size="small" inputProps={{ min: 1 }} onChange={(e) => qtyField.onChange(parseInt(e.target.value) || 1)} sx={textFieldSx} />}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`items.${index}.unitPrice`}
                        control={control}
                        render={({ field: priceField }) => <TextField {...priceField} type="number" size="small" InputProps={{ startAdornment: <InputAdornment position="start">DZD</InputAdornment> }} onChange={(e) => priceField.onChange(parseFloat(e.target.value) || 0)} sx={textFieldSx} />}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.bold, color: colors.textPrimary }}>{formatCurrency(calculateSubtotal(index))}</Typography>
                    </TableCell>
                    <TableCell>
                      {fields.length > 1 && (
                        <IconButton size="small" onClick={() => remove(index)} sx={{ color: palette.error }}><DeleteIcon fontSize="small" /></IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 3, backgroundColor: colors.surfaceElevated, borderTop: `2px solid ${colors.border}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="overline" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono }}>TOTAL</Typography>
                <Typography variant="h4" sx={{ fontFamily: typography.fontFamily.mono, fontWeight: typography.fontWeight.extrabold, color: palette.primary }}>{formatCurrency(calculateTotal())}</Typography>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Additional Details */}
        <Card sx={{ backgroundColor: colors.surface, border: `2px solid ${colors.border}`, borderRadius: theme.radius.sm, p: 3, mb: 3, ...animations.slideUp(0.2) }}>
          <Typography variant="h6" sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold, color: colors.textPrimary, mb: 2 }}>PAYMENT DETAILS</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}>
            <Controller
              name="paymentTerms"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Payment Terms" sx={textFieldSx}>
                  <MenuItem value="immediate">Immediate</MenuItem>
                  <MenuItem value="net_15">Net 15</MenuItem>
                  <MenuItem value="net_30">Net 30</MenuItem>
                  <MenuItem value="net_45">Net 45</MenuItem>
                  <MenuItem value="net_60">Net 60</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </TextField>
              )}
            />
            <Controller
              name="notes"
              control={control}
              render={({ field }) => <TextField {...field} label="Notes" multiline rows={3} sx={textFieldSx} />}
            />
          </Box>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', ...animations.slideUp(0.25) }}>
          <Button variant="outlined" onClick={() => navigate('/app/invoices')} sx={{ borderColor: colors.border, color: colors.textPrimary, px: 4, py: 1.5 }}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={updateMutation.isPending} sx={{ backgroundColor: palette.primary, color: '#FFFFFF', fontWeight: typography.fontWeight.bold, textTransform: 'uppercase', px: 4, py: 1.5, borderRadius: theme.radius.sm, '&:hover': { backgroundColor: alpha(palette.primary, 0.8) } }}>
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
